import React, { useRef } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as Info } from '../../../assets/img/icons/common/InfoOutlined.svg';
import { ReactComponent as Close } from '../../../assets/img/icons/common/Close.svg';
import { ReactComponent as ZoomIn } from '../../../assets/img/icons/player/ZoomIn.svg';
import { ReactComponent as Screenshot } from '../../../assets/img/icons/player/Screenshot.svg';
import { ReactComponent as Refresh } from '../../../assets/img/icons/player/Refresh.svg';
import { ReactComponent as Fullscreen } from '../../../assets/img/icons/player/Fullscreen.svg';
import { ReactComponent as Circle } from '../../../assets/img/icons/common/Circle.svg';

import { ButtonOnVideo, MainVideoButton } from '../../../components';
import { makeVideScreenshot } from '../../../utils';
import useMouseInactivity from '../../../hooks/useMouseInactivity';
import ResolutionDropdownButton from '../../PlayerControls/ResolutionDropdownButton';
import PlayerName from '../../HistoryPlayer/components/PlayerHeader/PlayerName';
import './ManagerPlayerControls.scss';

export const ManagerPlayerControls = ({
  mobile,
  camera,
  videoRef,
  isMetaLoaded,
  isFullscreen,
  changeCameraValue,
  toggleFullScreen,
  onCloseCamera,
  onResetScale,
  onRestartStream,
  cameraResolution,
  displayLiveBtn,
  disableLiveBtn,
}) => {
  const controlsRef = useRef(null);
  const resolutionLoading = !!cameraResolution?.loading;
  const tooltipId = `camera-tooltip-${camera.key}`;
  useMouseInactivity(controlsRef);
  
  return (
    <div ref={controlsRef} className="manager-player__controls">
      <div className="manager-player__controls__header">
        <ButtonOnVideo
          className="manager-player__controls__header-button"
          active={camera.showInfo}
          Icon={Info}
          onClick={(e) => {
            e.stopPropagation();
            changeCameraValue(camera.key, 'showInfo', !camera.showInfo);
          }}
          tooltip={{
            id: tooltipId,
            title: 'Information',
            place: 'bottom',
          }}
        />
        
        <PlayerName name={camera.name} />

        <ButtonOnVideo
          className="manager-player__controls__header-button"
          active={isFullscreen}
          Icon={Fullscreen}
          onClick={toggleFullScreen}
          disabled={!isFullscreen && (camera.hasStartStreamError || !isMetaLoaded)}
          tooltip={{
            id: tooltipId,
            title: isFullscreen ? 'Exit full screen' : 'Full screen',
            place: 'bottom',
          }}
        />
      </div>

      <div className="manager-player__controls__footer">
        {displayLiveBtn ? (
          <MainVideoButton
            content={
              <>
                <Circle />
                <span>Live</span>
              </>
            }
            onClick={e => {
              e.stopPropagation();
              changeCameraValue(camera.key, 'live', false);
            }}
            tooltip={{
              id: tooltipId,
              title: 'History',
              place: 'top',
            }}
            disabled={disableLiveBtn}
            active
          />
        ) : null}
        
        {!mobile && (
          <MainVideoButton
            Icon={ZoomIn}
            active={camera.enableScaling}
            disabled={camera.hasStartStreamError || resolutionLoading}
            tooltip={{
              id: tooltipId,
              title: camera.enableScaling ? 'Disable zoom in & out' : 'Enable zoom in & out',
              place: 'top',
            }}
            onClick={(e) => {
              e.stopPropagation();
              camera.enableScaling && onResetScale();
              changeCameraValue(camera.key, 'enableScaling', !camera.enableScaling);
            }}
          />
        )}

        <MainVideoButton
          Icon={Screenshot}
          disabled={camera.hasStartStreamError || resolutionLoading}
          tooltip={{
            id: tooltipId,
            title: 'Screenshot',
            place: 'top',
          }}
          onClick={(e) => {
            e.stopPropagation();
            makeVideScreenshot(videoRef);
          }}
        />

        <MainVideoButton
          Icon={Refresh}
          tooltip={{
            id: tooltipId,
            title: 'Restart',
            place: 'top',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRestartStream();
          }}
          disabled={resolutionLoading}
        />
        
        {cameraResolution ? (
          <ResolutionDropdownButton cameraResolution={cameraResolution} cameraTooltipKey={camera.key} />
        ) : null}
  
        <MainVideoButton
          Icon={Close}
          active={true}
          tooltip={{
            id: tooltipId,
            title: 'Close',
            place: 'top',
          }}
          onClick={onCloseCamera}
        />
      </div>

      {!mobile && (
        <Tooltip
          id={tooltipId}
          style={{ fontSize: '12px', zIndex: 1100 }}
          hidden={mobile}
        />
      )}
    </div>
  );
};
